import { ClientPaginatedTable } from '@avanta/shared/components/ClientPaginatedTable';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { t } from '@avanta/shared/i18n';
import { getCenterName, getStatusBadge } from '@avanta/shared/utils/appointments';
import { type ColumnDef } from '@tanstack/react-table';
import { Calendar } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { APPOINTMENT_TABLE_CLASS_NAME, useAppointmentAction } from 'hooks/useAppointmentAction';
import { useFetchAppointmentsHistory } from 'hooks/useFetchAppointmentsHistory';
import { type Appointment, type EnterpriseAppointmentFilter } from 'types/appointments';
import {
  getAppointmentFilterOptions,
  getSessionStatusColumnFilter,
  matchesAppointmentFilter,
} from 'utils/appointments';
import { formatUnifiedForDisplay } from 'utils/date';
import { SessionTableActions } from './SessionTableActions';
import { APPOINTMENT_ACTION_OPTIONS } from './constants';

const getTableColumns = (): ColumnDef<Appointment>[] => [
  {
    id: 'date',
    accessorKey: 'dateTime',
    enableSorting: true,
    header: t('AppointmentsTable.date.label', { ns: LOCIZE_NS.APPOINTMENTS }),
    accessorFn: row => formatUnifiedForDisplay(row.dateTime),
    cell: ({ row }) => formatUnifiedForDisplay(row.original.dateTime),
  },
  {
    id: 'psychologist',
    accessorKey: 'psychologist',
    meta: { truncation: { maxWidthClassName: 'max-w-34' } },
    header: t('AppointmentsTable.facultative.label', { ns: LOCIZE_NS.APPOINTMENTS }),
    accessorFn: row =>
      `${row.psychologist.psychologist.name} ${row.psychologist.psychologist.surname}`,
    cell: ({ row }): React.ReactElement => {
      const name = `${row.original.psychologist.psychologist.name} ${row.original.psychologist.psychologist.surname}`;
      return <span className="truncate">{name}</span>;
    },
  },
  {
    id: 'type',
    accessorKey: 'bookedContactMethod',
    header: t('AppointmentsTable.type.label', { ns: LOCIZE_NS.APPOINTMENTS }),
    accessorFn: row =>
      t(`AppointmentCard.types.${row.bookedContactMethod}`, { ns: LOCIZE_NS.APPOINTMENTS }),
    cell: ({ row }) =>
      t(`AppointmentCard.types.${row.original.bookedContactMethod}`, {
        ns: LOCIZE_NS.APPOINTMENTS,
      }),
  },
  {
    id: 'status',
    accessorKey: 'status',
    enableColumnFilter: true,
    header: t('AppointmentsTable.status.label', { ns: LOCIZE_NS.APPOINTMENTS }),
    meta: {
      columnFilter: getSessionStatusColumnFilter(),
    },
    cell: ({ row }) => getStatusBadge(row.original),
  },
  {
    id: 'center',
    accessorKey: 'center',
    header: t('AppointmentsTable.center.label', { ns: LOCIZE_NS.APPOINTMENTS }),
    meta: { truncation: { maxWidthClassName: 'max-w-19' } },
    accessorFn: row => getCenterName(row.bookedContactMethod, row.center),
    cell: ({ row }) => (
      <span className="truncate">
        {getCenterName(row.original.bookedContactMethod, row.original.center)}
      </span>
    ),
  },
  {
    id: 'actions',
    header: t('AppointmentsTable.actions.label', { ns: LOCIZE_NS.APPOINTMENTS }),
    cell: ({ row }) => <SessionTableActions appointment={row.original} />,
  },
];

export const AppointmentsHistory = (): React.ReactElement => {
  const params = useParams();
  const patientId = params?.id ?? '';
  const { data: appointments = [], isLoading } = useFetchAppointmentsHistory(patientId);
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);
  const { runAppointmentAction } = useAppointmentAction<Appointment>(APPOINTMENT_ACTION_OPTIONS);
  const columns = useMemo(() => getTableColumns(), []);

  return (
    <ClientPaginatedTable<Appointment, EnterpriseAppointmentFilter>
      data={appointments}
      columns={columns}
      onRowAction={runAppointmentAction}
      isLoading={isLoading}
      emptyTitle={t('Common.noResultsFound', { ns: LOCIZE_NS.COMMON })}
      emptyMessage={t('AppointmentsHistory.empty', { ns: LOCIZE_NS.INSURANCE })}
      title={t('AppointmentsHistory.heading')}
      renderSubtitle={count => t('AppointmentsHistory.totalResults', { count })}
      icon={Calendar}
      tableClassName={APPOINTMENT_TABLE_CLASS_NAME}
      withSearch
      searchPlaceholder="AppointmentsHistory.search.placeholder"
      searchPlaceholderNs={LOCIZE_NS.INSURANCE}
      filter={{
        accessorKey: 'status',
        allLabel: t('SessionsPage.filters.all'),
        options: getAppointmentFilterOptions(),
        filterFn: matchesAppointmentFilter,
      }}
    />
  );
};
